import { Stack, Typography } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { SelectOneAppHook } from 'components/core/search/apps'
import { DisplayUserNames } from 'components/core/search/users'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { formatAppSubtitle } from 'components/pages/app/list'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { QUERIES } from 'components/pages/explore/query/Query'
import { StructuredSearchStyles } from 'components/pages/explore/query/styles'
import { AppValue } from 'lib/actions/apps'
import { useInitialFilters } from 'lib/search'
import { SearchAppsExpandedRequest, SearchAppsWithDeactivatedOwnerRequest } from 'pbts/c1/api/app/v1/app'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppsWithDeactivatedOwner, AppAndOwnerValues } from 'redux_toolkit/apps'

export const cols: C1TableReduxColumn<AppAndOwnerValues>[] = [
    {
        field: 'app',
        label: <FormattedMessage id="components_app_list_name" defaultMessage="Application" />,
        Component: ({ row }) => <AppDisplayName app={row.App} />,
    },
    {
        field: 'source',
        label: <FormattedMessage id="components_app_list_source" defaultMessage="Source" />,
        Component: ({ row }) => (row.App.parentApp
            ? <AppDisplayName app={row.App.parentApp} />
            : <C1EmptyIndicator />),
    },
    {
        field: 'app_owner',
        label: <FormattedMessage id="components_app_list_source" defaultMessage="App owner" />,
        Component: ({ row }) => (row.Owners && row.Owners.length > 0
            ? <DisplayUserNames expandable proposedOptions={row.Owners} />
            : <C1EmptyIndicator />),
    },
    {
        field: 'details',
        label: <FormattedMessage id="components_app_list_description" defaultMessage="Details" />,
        Component: ({ row }) => <Typography>{formatAppSubtitle(row.App)}</Typography>,
    },
]

const AppsWithDeactivatedOwnersList: FC<{ appId: string | undefined }> = ({ appId }) => {
    const [page, setPage] = useState<number>(0)

    const { data, isError, isLoading, getPage, canLoadMore, error } = searchAppsWithDeactivatedOwner.useSearchAppsWithDeactivatedOwner(
        SearchAppsWithDeactivatedOwnerRequest.create({
            appId,
            expandMask: { paths: ['*'] },
        }),
        {},
    )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AppsWithDeactivatedOwners)

    const table = useMemo(
        () => (
            <C1TableRedux<AppAndOwnerValues>
                data={data}
                cols={cols}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                onPageChange={onPageChange}
                error={error}
                page={page}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    return <List<AppAndOwnerValues> table={table} />
}

export const AppsWithDeactivatedOwnersReport: FC = () => {
    const initialFilter = useInitialFilters(SearchAppsExpandedRequest)

    const [selectedAppId, setSelectedAppId] = useState<string | undefined>(initialFilter.ids?.[0])

    const onSelect = useCallback((app: AppValue | null): void => setSelectedAppId(app?.id), [])

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.APPS_WITH_DEACTIVATED_OWNERS,
            request: {
                oneofKind: 'searchAppsWithDeactivatedOwnerRequest',
                searchAppsWithDeactivatedOwnerRequest: SearchAppsWithDeactivatedOwnerRequest.create({
                    appId: selectedAppId,
                    expandMask: { paths: ['*'] },
                }),
            },
        })
    }, [selectedAppId])

    return <>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-end" pr={1}>
            <SelectOneAppHook
                onSelected={onSelect}
                initAppId={initialFilter.ids?.[0]}
                sx={sxc(StructuredSearchStyles.selectApp, StructuredSearchStyles.filterInputs)}
            />
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Stack>
        <AppsWithDeactivatedOwnersList appId={selectedAppId} />
    </>
}
